// Live room availability for Cloudbeds-backed accommodation (lifted from
// the widget's state/booking.ts in parity Phase 2). Cloudbeds is the source
// of truth across every channel/OTA, so booking surfaces LIVE-CHECK it
// instead of trusting Vertu's local inventory — this is what prevents the
// staff app (or widget) overselling a room type that an OTA just took.
// Calls the anon-safe cloudbeds-proxy `widget_availability` action, which
// resolves the integration from the public service_id server-side.

import type { BookingClient } from "./client";
import type { BookingWindow } from "./booking-rpc";

export type RoomAvailability = {
  // false when the service isn't Cloudbeds-backed (or we couldn't check) —
  // callers then fall back to local capacity checks and show all rooms.
  cloudbeds: boolean;
  // Vertu unit slugs with >= 1 room free for the window. null = don't
  // filter (not Cloudbeds-backed, or the check failed — fail open so a
  // Cloudbeds outage can't block every booking; the reservation push is
  // the hard guard).
  availableSlugs: Set<string> | null;
};

// Mirror the admin sync's unitSlug so a Cloudbeds room_type_id maps back to
// the Vertu unit it was imported as (service_units.slug = 'cloudbeds-<id>').
export function cloudbedsUnitSlug(roomTypeId: string): string {
  const cleaned = String(roomTypeId)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `cloudbeds-${cleaned || "room"}`;
}

export async function fetchRoomAvailability(
  client: BookingClient,
  input: {
    serviceId: string;
    window: BookingWindow;
  },
): Promise<RoomAvailability> {
  try {
    const { data: raw, error } = await client.functions.invoke(
      "cloudbeds-proxy",
      {
        body: {
          action: "widget_availability",
          service_id: input.serviceId,
          starts_at: input.window.starts_at,
          ends_at: input.window.ends_at,
          // Query raw date inventory (1 adult), NOT the full party. A room
          // type is bookable whenever it has any room free, because a party
          // is composed across rooms — of DIFFERENT types too (e.g. a 2-pax
          // + a 3-pax for a party of 5). Passing the whole party as `adults`
          // makes Cloudbeds return rooms_available=0 for every room whose
          // max occupancy is below the party size, wrongly hiding available
          // rooms as "not available".
          adults: 1,
        },
      },
    );
    const data = raw as {
      ok?: boolean;
      cloudbeds?: boolean;
      rooms?: unknown;
    } | null;
    if (error || !data?.ok || data.cloudbeds === false) {
      return { cloudbeds: false, availableSlugs: null };
    }
    const rooms = Array.isArray(data.rooms) ? data.rooms : [];
    const availableSlugs = new Set<string>(
      rooms
        .filter(
          (r: { rooms_available?: number }) =>
            Number(r.rooms_available ?? 0) > 0,
        )
        .map((r: { room_type_id: string }) =>
          cloudbedsUnitSlug(r.room_type_id),
        ),
    );
    return { cloudbeds: true, availableSlugs };
  } catch {
    return { cloudbeds: false, availableSlugs: null };
  }
}
